import posthog from 'posthog-js'

import type {
  OnboardingStepName,
  KYCStepName,
  ELoginType,
  ELoginFrom,
} from './analyticsConstants'
import { OnboardingStepIndex, KYCStepIndex } from './analyticsConstants'
import { AnalyticsEvents } from './events'

// ============================================================================
// Auth Events
// ============================================================================

/**
 * identifyUser
 * -
 * Identify user by email
 *
 * @param email - user's email
 * @param properties - additional properties to identify the user
 */
export function identifyUser(
  email: string,
  properties?: Record<string, any>,
): void {
  posthog.identify(email, properties)
}

/**
 * resetUser
 * -
 * Reset user identity (call on logout)
 */
export function resetUser(): void {
  posthog.reset()
}

// ============================================================================
// Pageview Events
// ============================================================================

/**
 * trackVslSetPasswordViewed
 * -
 * Track when a user views the set password page (only available to users coming from VSL)
 */
export function trackVslSetPasswordViewed(): void {
  posthog.capture(AnalyticsEvents.SET_VSL_PASSWORD_VIEWED)
}

/**
 * trackLoginViewedFrom
 * -
 * Track when a user views the login page with query params
 *
 * @param params - query parameters (type, from)
 */
export function trackLoginViewedFrom(params: {
  type?: ELoginType
  from?: ELoginFrom
}): void {
  posthog.capture(AnalyticsEvents.LOGIN_VIEWED_FROM, params)
}

/**
 * trackPageview
 * -
 * Manual pageview tracking
 *
 * @param path - optional path to track
 */
export function trackPageview(path?: string): void {
  posthog.capture('$pageview', path ? { $current_url: path } : undefined)
}

// ============================================================================
// Onboarding Events (frontend tracks views only)
// ============================================================================

/**
 * trackOnboardingStepViewed
 * -
 * Track onboarding step viewed (for drop-off analysis)
 * Backend tracks onboarding_step_completed
 *
 * @param stepName - name of the onboarding step
 */
export function trackOnboardingStepViewed(stepName: OnboardingStepName): void {
  posthog.capture(AnalyticsEvents.ONBOARDING_STEP_VIEWED, {
    step_name: stepName,
    step_index: OnboardingStepIndex[stepName],
  })
}

/**
 * trackKYCStepViewed
 * Track KYC verification step viewed
 * Used for KYC funnel analysis (separate from onboarding)
 */
export function trackKYCStepViewed(stepName: KYCStepName): void {
  posthog.capture(AnalyticsEvents.KYC_STEP_VIEWED, {
    step_name: stepName,
    step_index: KYCStepIndex[stepName],
  })
}

// ============================================================================
// Paywall / Checkout Events (frontend intent)
// ============================================================================

/**
 * trackPricingTableViewed
 * -
 * Track when pricing table / paywall is displayed
 */
export function trackPricingTableViewed(): void {
  posthog.capture(AnalyticsEvents.PRICING_TABLE_VIEWED)
}

export function trackCreditsTopupStarted(): void {
  posthog.capture(AnalyticsEvents.CREDITS_TOPUP_STARTED)
}

// ============================================================================
// Report Events (frontend intent only; backend tracks outcomes)
// ============================================================================

/**
 * Track report run initiated (user intent)
 * Fire when user triggers report generation (click/submit)
 */
export function trackReportRunInitiated(): void {
  posthog.capture(AnalyticsEvents.REPORT_RUN_INITIATED)
}
